import { getQueryKey_escape_hatch as getQueryKeySearchProductsV3 } from "@faire/web-api/api/v3/search/products/post-hooks";
import { trackRetailersPageViewView } from "@faire/web-api/events/retailers/view/pageView";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import React, { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { RetailerFooter } from "@faire/retailer/components/Footer/RetailerFooter";
import { LazySearchProductsError } from "@faire/retailer/components/SearchProducts/SearchProductsError";
import { BlockInheritRow } from "@faire/retailer/components/SearchProducts/SearchProductsStyles";
import { SidebarAndContent } from "@faire/retailer/components/SidebarAndContent";
import { PageInitialized } from "@faire/retailer/events";
import { ExplorePageContent } from "@faire/retailer/views/explore-page/__internal__/ExplorePageContent";
import { useExplorePageProductsQuery } from "@faire/retailer/views/explore-page/__internal__/useExplorePageProductsQuery";

import { useExplorePageProductsRequest } from "./__internal__/useExplorePageProductsRequestKey";

interface IDeskTopSizes {
  desktopWidth: number;
  xlargeWidth: number;
}

const SIDE_BAR_SIZES: IDeskTopSizes = {
  desktopWidth: 260,
  xlargeWidth: 300,
};

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const ExplorePage: React.FC = () => {
  const {
    isSearchPending,
    isSearchError,
    searchDataUpdatedAt,
    totalPrimaryResults,
  } = useExplorePageProductsQuery();

  const request = useExplorePageProductsRequest();
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "page-explore",
    pageLoadKey: JSON.stringify(getQueryKeySearchProductsV3(request)),
    dataUpdatedAt: searchDataUpdatedAt,
    numComponents: 1,
    ignoreImages: !isSearchPending && totalPrimaryResults === 0,
    isPageLoading: isSearchPending,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
  });

  useEffect(() => {
    if (!isSearchPending) {
      PageInitialized.publish();
    }
  }, [isSearchPending]);

  const { pathname } = useLocation();

  useEffect(() => {
    trackRetailersPageViewView(
      "",
      "",
      request.query ?? "",
      "explore",
      pathname,
      "page-explore"
    );
  }, [request.query, pathname]);

  if (isSearchError) {
    return (
      <Suspense fallback={null}>
        <LazySearchProductsError />
      </Suspense>
    );
  }
  return (
    <div style={{ width: "100%" }}>
      <SidebarAndContent
        desktopWidth={`${SIDE_BAR_SIZES.desktopWidth}px`}
        xLargeWidth={`${SIDE_BAR_SIZES.xlargeWidth}px`}
        content={
          <ExplorePageContent
            loadTimingRef={isSearchPending ? undefined : loadTimingRef}
          />
        }
        sidebar={null}
        noMaxWidth
      />

      <BlockInheritRow>
        <RetailerFooter hideReferral />
      </BlockInheritRow>
    </div>
  );
};
